import { useEffect, useMemo, useState } from "react";
import { Alert, Button, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { ArrowBack, Delete, Edit, Group, PersonAdd, Sync } from "@mui/icons-material";
import { DataTable } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { ACOES_DE_ESCOPO, MetasResponsavelModal } from "@/components/metas/MetasResponsavelModal";
import usePagination from "@/hooks/pagination";
import { fetchAllUsers } from "@/api/allUsers";
import { getUsersByModule } from "@/api/module.service";
import type { User } from "@/api/user.service";
import {
  alterarFluxo,
  deleteResponsavel,
  listResponsaveis,
  obterFluxo,
  saveResponsavel,
  type AcaoResponsavel,
  type ConfiguracaoFluxo,
  type Responsavel,
} from "@/api/metas.service";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";

// Permissões do fluxo de aprovação das metas, organizadas pela ação: uma linha
// por ação e, dentro dela, os usuários vinculados. O cadastro é global por cliente
// e guarda um registro por usuário e ação (metas.listar_responsaveis /
// salvar_responsavel / remover_responsavel), cada um com o próprio escopo de
// lojas e categorias — mexer no de uma ação não muda o das outras.

const MODULE_ID = "metas";

const ACOES: { id: AcaoResponsavel; rotulo: string; descricao: string }[] = [
  { id: "ADMINISTRAR", rotulo: "Administrar o fluxo", descricao: "Direciona o processo entre etapas e vê todo o cálculo." },
  { id: "CALCULAR", rotulo: "Calcular", descricao: "Altera valores e encaminha as metas para revisão." },
  { id: "REVISAR", rotulo: "Revisar", descricao: "Valida o escopo ou o devolve ao cálculo." },
  { id: "APROVAR", rotulo: "Aprovar", descricao: "Aprova o escopo ou o devolve à revisão." },
  { id: "PUBLICAR", rotulo: "Publicar", descricao: "Publica quando todas as aprovações obrigatórias terminam." },
  { id: "REABRIR", rotulo: "Reabrir", descricao: "Reinicia o ciclo no cálculo, com justificativa." },
];

type Acao = (typeof ACOES)[number];

interface LinhaUsuario {
  usuario: User;
  responsavel: Responsavel;
}

export function MetasResponsaveisTab({ onFluxoChange }: { onFluxoChange?: (habilitado: boolean) => void }) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const lista = usePagination();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [permissoes, setPermissoes] = useState<Responsavel[]>([]);
  const [fluxo, setFluxo] = useState<ConfiguracaoFluxo | null>(null);
  const [alternando, setAlternando] = useState(false);

  const [acaoAberta, setAcaoAberta] = useState<Acao | null>(null);
  // Modal de adicionar/editar por cima da lista da ação.
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<User | null>(null);
  const [removendo, setRemovendo] = useState<LinhaUsuario | null>(null);
  const [removendoEmAndamento, setRemovendoEmAndamento] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [todos, doModulo, cadastrados, configuracao] = await Promise.all([
          fetchAllUsers(),
          getUsersByModule(MODULE_ID, clientId),
          listResponsaveis(clientId),
          obterFluxo(clientId),
        ]);
        if (cancelado) return;
        const comModulo = new Set(doModulo.map((m) => m.user_id));
        const comPermissao = new Set((cadastrados ?? []).map((r) => r.userId));
        // Quem já tem permissão fica na lista mesmo sem o módulo, senão não haveria como removê-lo.
        setUsuarios(todos.filter((u) => comModulo.has(u.id) || comPermissao.has(u.id)));
        setPermissoes(cadastrados ?? []);
        setFluxo(configuracao);
      } catch {
        if (!cancelado) setErro(true);
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [clientId]);

  useEffect(() => {
    setTotal(ACOES.length);
  }, [setTotal]);

  const usuarioPorId = useMemo(() => new Map(usuarios.map((u) => [u.id, u])), [usuarios]);

  const doAcao = (acao: AcaoResponsavel): LinhaUsuario[] =>
    permissoes
      .filter((r) => r.acao === acao)
      .flatMap((r) => {
        const usuario = usuarioPorId.get(r.userId);
        return usuario ? [{ usuario, responsavel: r }] : [];
      })
      .sort((a, b) => a.usuario.name.localeCompare(b.usuario.name));

  const linhasDaAcao = acaoAberta ? doAcao(acaoAberta.id) : [];
  const permissoesDaAcao = useMemo(
    () => Object.fromEntries(permissoes.filter((r) => r.acao === acaoAberta?.id).map((r) => [r.userId, r])),
    [permissoes, acaoAberta],
  );

  useEffect(() => {
    lista.setTotal(linhasDaAcao.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhasDaAcao.length]);

  const mesmaPermissao = (a: Responsavel, b: Responsavel) => a.userId === b.userId && a.acao === b.acao;

  const salvar = async (responsavel: Responsavel) => {
    try {
      const salvo = await saveResponsavel(responsavel, clientId);
      setPermissoes((atual) => [...atual.filter((r) => !mesmaPermissao(r, salvo)), salvo]);
      notify("Permissão salva", "success");
    } catch {
      notify("Erro ao salvar a permissão", "error");
      throw new Error("Erro ao salvar a permissão");
    }
  };

  const alternarFluxo = async (habilitado: boolean) => {
    setAlternando(true);
    try {
      await alterarFluxo(habilitado, clientId);
      setFluxo(await obterFluxo(clientId));
      onFluxoChange?.(habilitado);
      notify(habilitado ? "Fluxo de aprovação ligado" : "Fluxo de aprovação desligado", "success");
    } catch (e) {
      notify(e instanceof Error && e.message ? e.message : "Erro ao alterar o fluxo de aprovação", "error");
      setFluxo(await obterFluxo(clientId).catch(() => fluxo));
    } finally {
      setAlternando(false);
    }
  };

  // Tirar o usuário de uma ação não mexe nas outras ações dele nem no escopo delas.
  const remover = async () => {
    if (!removendo) return;
    const { responsavel } = removendo;
    setRemovendoEmAndamento(true);
    try {
      await deleteResponsavel(responsavel.userId, responsavel.acao, clientId);
      setPermissoes((atual) => atual.filter((r) => !mesmaPermissao(r, responsavel)));
      notify("Usuário removido da ação", "success");
      setRemovendo(null);
    } catch {
      notify("Erro ao remover o usuário da ação", "error");
    } finally {
      setRemovendoEmAndamento(false);
    }
  };

  if (carregando) return <Sync />;
  if (erro) return <Alert severity="error">Não foi possível carregar as permissões.</Alert>;

  const paged = ACOES.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const linhasPaginadas = linhasDaAcao.slice(lista.page * lista.rowsPerPage, lista.page * lista.rowsPerPage + lista.rowsPerPage);
  const comEscopo = !!acaoAberta && ACOES_DE_ESCOPO.includes(acaoAberta.id);

  const listaDaAcao = acaoAberta && (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton aria-label="Voltar para as ações" onClick={() => setAcaoAberta(null)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {acaoAberta.rotulo}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => {
            setEditando(null);
            setFormAberto(true);
          }}
        >
          Adicionar usuário
        </Button>
      </Stack>
      <DataTable
        data={linhasPaginadas}
        columns={[
          { key: "nome", label: "Usuário", render: (row) => row.usuario.name },
          { key: "email", label: "Email", render: (row) => row.usuario.email },
          ...(comEscopo
            ? [
                { key: "lojas", label: "Lojas", render: (row: LinhaUsuario) => row.responsavel.lojas.length },
                { key: "categorias", label: "Categorias", render: (row: LinhaUsuario) => row.responsavel.categorias.length },
              ]
            : []),
        ]}
        total={lista.total}
        page={lista.page}
        rowsPerPage={lista.rowsPerPage}
        onPageChange={lista.setPage}
        onRowsPerPageChange={lista.setRowsPerPage}
        actions={(row) => (
          <>
            {comEscopo && (
              <Tooltip title="Editar escopo">
                <IconButton
                  aria-label={`Editar — ${row.usuario.name}`}
                  onClick={() => {
                    setEditando(row.usuario);
                    setFormAberto(true);
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Remover da ação">
              <IconButton aria-label={`Remover — ${row.usuario.name}`} color="error" onClick={() => setRemovendo(row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </>
        )}
      />
    </Stack>
  );

  const painelFluxo = fluxo && (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <FormControlLabel
        sx={{ alignSelf: "flex-start" }}
        control={
          <Switch
            checked={fluxo.habilitado}
            disabled={!fluxo.podeAlternar || fluxo.bloqueios.length > 0 || alternando}
            onChange={(_, marcado) => alternarFluxo(marcado)}
          />
        }
        label="Usar o fluxo de aprovação"
      />
      <Typography variant="body2" color="text.secondary">
        Desligado: o Criador de Meta calcula, ajusta e publica sem etapas de revisão e aprovação.
        {fluxo.habilitado && !fluxo.ativo && " Ligado, mas sem nenhum responsável cadastrado — o fluxo só passa a valer com ao menos um."}
      </Typography>
      {!fluxo.podeAlternar && (
        <Alert severity="info">Só o Criador de Meta com a permissão de administrar o fluxo pode ligar ou desligar.</Alert>
      )}
      {fluxo.podeAlternar && fluxo.bloqueios.length > 0 && (
        <Alert severity="warning">
          Para trocar o fluxo, resolva antes: {fluxo.bloqueios.join("; ")}.
        </Alert>
      )}
    </Stack>
  );

  return (
    <>
      {!acaoAberta && painelFluxo}
      {listaDaAcao ?? (
        <DataTable
          data={paged}
          columns={[
            { key: "rotulo", label: "Ação" },
            { key: "descricao", label: "O que faz" },
            { key: "usuarios", label: "Usuários", render: (row) => doAcao(row.id).length },
          ]}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          actions={(row) => (
            <IconButton
              aria-label={`Vincular pessoas — ${row.rotulo}`}
              onClick={() => {
                lista.setPage(0);
                setAcaoAberta(row);
              }}
            >
              <Group />
            </IconButton>
          )}
        />
      )}

      <MetasResponsavelModal
        acao={formAberto ? acaoAberta : null}
        clientId={clientId}
        usuarios={usuarios}
        permissoes={permissoesDaAcao}
        editando={editando}
        onClose={() => setFormAberto(false)}
        onSave={salvar}
      />

      <ConfirmModal
        open={!!removendo}
        onClose={() => setRemovendo(null)}
        onConfirm={remover}
        loading={removendoEmAndamento}
        title="Remover permissão"
        text={`${removendo?.usuario.name ?? "O usuário"} deixa de ter a ação "${acaoAberta?.rotulo ?? ""}" no fluxo de aprovação. Os ciclos já iniciados mantêm o escopo que registraram.`}
        btnText="Remover"
        btnColor="error"
      />
    </>
  );
}
